import { userEntryToNumber } from '@ebiz/pretty-num';
import { assert } from '@ember/debug';
import type { Column } from 'slickgrid';
import Validation from './validation.ts';
import type { ColumnFilter, DeviceData } from '../components/aditools-device-table.ts';

const _stripTrailingDecimal = function (item: string) {
  const dotted = item.split('.');
  return dotted.length === 2 && dotted[1] && dotted[1].length === 0 ? item.slice(0, -1) : item;
};

export default class EngineeringRangeValueFilter implements ColumnFilter<DeviceData> {
  defaultMinimum: string;
  defaultMaximum: string;
  minimumValue: string;
  maximumValue: string;
  qaName?: string;
  constructor(defaultMinimum?: string, defaultMaximum?: string, qaName?: string) {
    this.defaultMinimum = defaultMinimum ?? '';
    this.defaultMaximum = defaultMaximum ?? '';
    this.minimumValue = this.defaultMinimum;
    this.maximumValue = this.defaultMaximum;
    this.qaName = qaName;
  }

  populate(column: Column<DeviceData>): HTMLElement {
    if (!column) {
      throw 'column passed to populate is invalid';
    }
    const selection = $(this.generateDOMElement(column));
    selection.find('input:first-child').val(this.minimumValue);
    selection.find('input:nth-child(3)').val(this.maximumValue);
    assert('At least one element is always generated', selection[0]);
    return selection[0];
  }

  generateDOMElement(column: Column<DeviceData>): string {
    const inputQaElement = this.qaName ? `data-qa-element="device_table_${this.qaName}_input"` : '';
    return `<div class="slickgrid-filter-engineering">
      <input id="filter-${column.id}-val-min" class="slickgrid-filter-range-value" type="text" autocomplete="off" aria-label="${column.name} minimum value" ${inputQaElement} data-column-id="${column.id}" />
      <span class='slickgrid-filter-range-hyphen'>&#x2012;</span>
      <input id="filter-${column.id}-val-max" class="slickgrid-filter-range-value" type="text" autocomplete="off" aria-label="${column.name} maximium value" ${inputQaElement} data-column-id="${column.id}" />
    </div>`;
  }

  update(element: HTMLInputElement): void {
    const children = element.parentElement?.children;
    if (children) {
      this.minimumValue = (<HTMLInputElement>children[0]).value;
      this.maximumValue = (<HTMLInputElement>children[2]).value;
    }
  }

  unpack(packedValues: string): void {
    if (packedValues) {
      const initialValues = JSON.parse(packedValues);
      if (
        Object.prototype.hasOwnProperty.call(initialValues, 'minimumValue') &&
        Object.prototype.hasOwnProperty.call(initialValues, 'maximumValue')
      ) {
        this.minimumValue = initialValues.minimumValue;
        this.maximumValue = initialValues.maximumValue;
      }
    }
  }

  reset(): void {
    this.minimumValue = this.defaultMinimum;
    this.maximumValue = this.defaultMaximum;
  }

  hasValue(): boolean {
    return this.minimumValue !== '' || this.maximumValue !== '';
  }

  pack(): string {
    return JSON.stringify({ minimumValue: this.minimumValue, maximumValue: this.maximumValue });
  }

  filter(field: string): boolean {
    // Temporary workaround for Package Area
    const cleanedField = field ? (field.includes('mm') ? (field.split('mm')[0] ?? field) : field) : field;
    let fieldValue = cleanedField === '' || cleanedField === '-' ? 0 : userEntryToNumber(cleanedField, Infinity);
    fieldValue = isNaN(fieldValue) ? Infinity : fieldValue;
    const filterMinimumString = _stripTrailingDecimal(this.minimumValue);
    const filterMaximumString = _stripTrailingDecimal(this.maximumValue);
    if (
      Validation.validateFieldInput(filterMinimumString, 'ENGINEERING') ||
      Validation.validateFieldInput(filterMaximumString, 'ENGINEERING')
    ) {
      let filterMinValue = userEntryToNumber(filterMinimumString, Infinity);
      filterMinValue = isNaN(filterMinValue) ? Infinity : filterMinValue;
      let filterMaxValue = userEntryToNumber(filterMaximumString, Infinity);
      filterMaxValue = isNaN(filterMaxValue) ? Infinity : filterMaxValue;
      if (fieldValue || fieldValue === 0) {
        if (filterMinValue !== Infinity && filterMaxValue !== Infinity) {
          return fieldValue >= filterMinValue && fieldValue <= filterMaxValue;
        } else if (filterMinValue === Infinity && filterMaxValue !== Infinity) {
          return fieldValue <= filterMaxValue;
        } else if (filterMinValue !== Infinity && filterMaxValue === Infinity) {
          return fieldValue >= filterMinValue;
        }
      }
    }
    return true;
  }
}
